import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { centsFromDecimal, formatCurrency } from '@eis/core';
import { INSURED_PERSON_ROLE, POLICYHOLDER_ROLE } from './partners';

/**
 * View models for the Policen surface.
 *
 * # The word this file may not use
 *
 * "Version". Not in a label, not in a column header, not in a tooltip (SSOT I-6, service summary
 * §6 phase 2). A policy DOES carry a `businessVersion` and the surface has to be honest about
 * change over time — but it says so in the operations persona's own words: "Stand" for the belief
 * being read, "Zeitverlauf" for the segments. The configuration surface is where versions live.
 *
 * The `data-*` hooks and the German copy are in the i18n namespace; what is here is the reading.
 */

/** protojson writes enum NAMES prefixed with the enum's own name; the UI wants the bare value. */
export function bareEnum(value: string | undefined, prefix: string): string {
  if (value === undefined || value === '') return '';
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export const policyStatusOf = (policy: InstanceApi.Policy | undefined): string =>
  bareEnum(policy?.status, 'POLICY_STATUS_');

export const policyholderSourceOf = (policy: InstanceApi.Policy | undefined): string =>
  bareEnum(policy?.policyholder?.source, 'FIELD_SOURCE_');

/**
 * True when this policy's policyholder is the master's, resolved at read (decision D7).
 *
 * The marker matters because the value looks identical either way: the same partner id, in the
 * same column. What differs is that nobody can change it HERE — correcting the master changes it
 * for every member at once, and writing it onto the child would be the materialization D7
 * forbids.
 */
export const isInheritedPolicyholder = (policy: InstanceApi.Policy | undefined): boolean =>
  policyholderSourceOf(policy) === 'INHERITED';

export const isMember = (policy: InstanceApi.Policy | undefined): boolean =>
  (policy?.parentPolicyCode ?? '') !== '';

/** The directory's name for one partner in one role on this policy — or nothing, when nobody knew. */
function resolvedNameOf(
  policy: InstanceApi.Policy | undefined,
  roleErn: string,
  partnerId: string,
): string | undefined {
  if (partnerId === '') return undefined;
  const match = (policy?.partners ?? []).find(
    (partner) => partner.link?.roleErn === roleErn && partner.link?.partnerId === partnerId,
  );
  const name = match?.partnerName ?? '';
  return match?.partnerNameResolved === true && name !== '' ? name : undefined;
}

/**
 * What a list or a header shows for the insured person: the name when the directory knew one,
 * the id when it did not (SSOT §4.2) — the same rule PartnerList applies, applied to the one
 * role the platform names itself (P-6).
 */
export function insuredLabelOf(policy: InstanceApi.Policy | undefined): string {
  const id = policy?.insuredPerson?.partnerId ?? '';
  return resolvedNameOf(policy, INSURED_PERSON_ROLE, id) ?? id;
}

/**
 * The policyholder's label. A LOCAL policyholder is a link of the segment and reads from
 * `partners`; an INHERITED one is the master's (D7), not a link of this policy, so its name
 * travels on the slot itself (`policyholder.partnerName`, SDD-014 F-8). Either way: the id when
 * nobody knew — never a blank.
 */
export function policyholderLabelOf(policy: InstanceApi.Policy | undefined): string {
  const ref = policy?.policyholder;
  const id = ref?.resolvedPartnerId ?? '';
  const own = ref?.partnerName ?? '';
  if (ref?.partnerNameResolved === true && own !== '') return own;
  return resolvedNameOf(policy, POLICYHOLDER_ROLE, id) ?? id;
}

/** A row of the list, flattened for the grid. */
export interface PolicyRow {
  readonly id: string;
  readonly code: string;
  readonly status: string;
  readonly insuredPartnerId: string;
  readonly policyholderPartnerId: string;
  /** The insured person and the policyholder as the grid shows them: name when known, id otherwise. */
  readonly insuredLabel: string;
  /** The insured object as the service projects it (SDD-022 D-4): plate · VIN, a frame number, or ''. */
  readonly objectIdentifier: string;
  readonly policyholderLabel: string;
  readonly policyholderInherited: boolean;
  readonly parentPolicyCode: string;
  readonly tarifVersionErn: string;
  readonly monthlyPremium: string;
  readonly currency: string;
  /** The lead this policy was accepted from (SDD-013 R-4), or ''. */
  readonly originLeadCode: string;
  /** The first day of the slice the row shows (the cut's slice, SDD-017 §6). */
  readonly validFrom: string;
  /** The slice's product data — what the catalog's `data.<path>` columns read. */
  readonly data: Readonly<Record<string, unknown>> | undefined;
  /** Why the row answers a search (§8.5), when it does. */
  readonly match: InstanceApi.Match | undefined;
}

export function toRows(entries: readonly InstanceApi.PolicyListEntry[] | undefined): PolicyRow[] {
  return (entries ?? []).map((entry) => {
    const policy = entry.policy;
    return {
      id: policy?.code ?? '',
      code: policy?.code ?? '',
      status: policyStatusOf(policy),
      insuredPartnerId: policy?.insuredPerson?.partnerId ?? '',
      policyholderPartnerId: policy?.policyholder?.resolvedPartnerId ?? '',
      insuredLabel: insuredLabelOf(policy),
      objectIdentifier: entry.objectIdentifier ?? '',
      policyholderLabel: policyholderLabelOf(policy),
      policyholderInherited: isInheritedPolicyholder(policy),
      parentPolicyCode: policy?.parentPolicyCode ?? '',
      tarifVersionErn: policy?.configuration?.resolvedTarifVersionErn ?? '',
      monthlyPremium: policy?.premium?.monthly?.amount ?? '',
      currency: policy?.premium?.monthly?.currency ?? '',
      originLeadCode: policy?.originLeadCode ?? '',
      validFrom: entry.snapshot?.validFrom ?? '',
      data: policy?.data,
      match: entry.match,
    };
  });
}

/** A catalog key's value in a row's product data: `data.bike.bike_value` → `4200`. */
export function dataValueOf(
  data: Readonly<Record<string, unknown>> | undefined,
  key: string,
): string | number | boolean | undefined {
  let current: unknown = data;
  for (const segment of key.replace(/^data\./, '').split('.')) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean'
    ? current
    : undefined;
}

/**
 * A search hit in ANOTHER slice than the row shows (§8.5, PPL-878): the slice's first and last day
 * (`validTo` is exclusive), the field and the matched value — what the row's chip says and links to.
 */
export interface OutOfSliceHit {
  readonly field: string;
  readonly value: string;
  readonly validFrom: string;
  /** The last day the slice covers, or undefined when it is open-ended. */
  readonly lastDay: string | undefined;
}

export function outOfSliceHit(
  match: InstanceApi.Match | undefined,
  lastDayOf: (validTo: string) => string,
): OutOfSliceHit | undefined {
  const validFrom = match?.slice?.validFrom ?? '';
  if (match?.isCurrentSlice !== false || validFrom === '') return undefined;
  const validTo = match.slice?.validTo ?? '';
  return {
    field: match.field ?? '',
    value: match.snippet ?? '',
    validFrom,
    lastDay: validTo === '' ? undefined : lastDayOf(validTo),
  };
}

/**
 * The segments a reader should see, newest belief first, with superseded ones hidden by default.
 *
 * Superseded is never deleted (decision S-6) — GoBD and premium reproducibility need every
 * historical belief reconstructable — but it is also not what somebody opening a policy is asking
 * about. Hiding it by default and offering it explicitly is the difference between an audit tool
 * and a screen that shows four rows where one is true.
 */
export function visibleSegments(
  segments: readonly InstanceApi.PolicySegment[] | undefined,
  includeSuperseded: boolean,
): readonly InstanceApi.PolicySegment[] {
  const all = segments ?? [];
  return includeSuperseded ? all : all.filter((segment) => segment.snapshot?.current !== false);
}

export const isSuperseded = (segment: InstanceApi.PolicySegment): boolean =>
  segment.snapshot?.current === false;

/**
 * The premium with the period it is owed for (SDD-015 B-6, Q-3): `amount` per `period`, falling
 * back to the derived `monthly` for a snapshot written before the field existed — which reads as
 * PER_MONTH, the same rule the service applies. The suffix is the console's word for the period
 * (`policies.premiumPeriod.*`), the amount is `formatCurrency`'s: "8,59 € / Monat".
 *
 * An amount that does not parse is shown RAW rather than as 0,00 €.
 */
export function premiumWithPeriod(
  premium: InstanceApi.Premium | undefined,
  locale: string,
  periodWord: (period: string) => string,
): string {
  const money = premium?.amount ?? premium?.monthly;
  const value = money?.amount ?? '';
  const currency = money?.currency ?? '';
  if (value === '') return '—';
  const cents = centsFromDecimal(value);
  const amount =
    cents === null
      ? currency === ''
        ? value
        : `${value} ${currency}`
      : formatCurrency(cents, currency === '' ? 'EUR' : currency, locale);
  const period =
    premium?.period === undefined || premium.period === '' ? 'PER_MONTH' : premium.period;
  return `${amount} ${periodWord(period)}`.trim();
}
